import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";

const PROPERTY_TYPES = [
  { v: "apartment", l: "Apartment" },
  { v: "villa", l: "Villa" },
  { v: "townhouse", l: "Townhouse" },
  { v: "office", l: "Office" },
  { v: "retail", l: "Retail / F&B" },
  { v: "warehouse", l: "Warehouse" },
  { v: "other", l: "Other" },
];

const SIZE_OPTIONS = [
  { v: "studio", l: "Studio / Small" },
  { v: "1br", l: "1 Bedroom" },
  { v: "2br", l: "2 Bedrooms" },
  { v: "3br", l: "3 Bedrooms" },
  { v: "4br_plus", l: "4+ Bedrooms" },
  { v: "lt_500", l: "Office < 500 sqft" },
  { v: "500_2000", l: "500 – 2,000 sqft" },
  { v: "2000_5000", l: "2,000 – 5,000 sqft" },
  { v: "gt_5000", l: "> 5,000 sqft" },
];

const SERVICE_OPTIONS = [
  { v: "deep-cleaning", l: "Deep Cleaning" },
  { v: "move-in-out", l: "Move-In / Move-Out Cleaning" },
  { v: "general-cleaning", l: "Regular / General Cleaning" },
  { v: "post-construction", l: "Post-Construction Cleaning" },
  { v: "carpet-sofa", l: "Carpet & Sofa Shampoo" },
  { v: "office-cleaning", l: "Office Cleaning" },
  { v: "disinfection", l: "Disinfection / Sanitisation" },
  { v: "other", l: "Other" },
];

const LOCATIONS = [
  "Dubai Marina",
  "JLT",
  "Downtown / Business Bay",
  "DIFC",
  "Palm Jumeirah",
  "JBR",
  "Mirdif / Al Khawaneej",
  "Al Barsha",
  "Arabian Ranches",
  "Emirates Hills",
  "JVC / JVT",
  "Damac Hills / Dubai Hills",
  "Deira / Bur Dubai",
  "Silicon Oasis",
  "Jumeirah / Umm Suqeim",
  "Other Dubai",
];

export const CleaningLeadForm = ({ pageSource = "google-ads-cleaning-dubai" }) => {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    propertyType: "",
    size: "",
    location: "",
    serviceRequired: "",
    preferredDate: "",
    notes: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.propertyType || !form.serviceRequired) {
      toast.error("Please fill in name, phone, property type and service.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        service: form.serviceRequired,
        page_source: pageSource,
        language: lang,
        // Structured columns — no more packing into `message`
        property_type: form.propertyType || null,
        size: form.size || null,
        location: form.location || null,
        service_required: form.serviceRequired,
        preferred_date: form.preferredDate || null,
        notes: form.notes || null,
      };
      if (form.email) payload.email = form.email;

      await submitLead(payload);
      toast.success("Request received", {
        description: "Our team will call you back within 30 minutes.",
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        propertyType: "",
        size: "",
        location: "",
        serviceRequired: "",
        preferredDate: "",
        notes: "",
      });
    } catch {
      toast.error(t("common.errorMsg"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="cleaning-lead-form" onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Full Name *</Label>
          <Input
            data-testid="cleaning-form-name"
            value={form.name}
            onChange={update("name")}
            required
            className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Phone (UAE) *</Label>
          <Input
            data-testid="cleaning-form-phone"
            value={form.phone}
            onChange={update("phone")}
            required
            placeholder="+971 ..."
            className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
          Email <span className="ms-1 text-[10px] text-slate-400 normal-case">(optional)</span>
        </Label>
        <Input
          data-testid="cleaning-form-email"
          type="email"
          value={form.email}
          onChange={update("email")}
          className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Property Type *</Label>
          <Select value={form.propertyType} onValueChange={set("propertyType")}>
            <SelectTrigger
              data-testid="cleaning-form-property"
              className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
            >
              <SelectValue placeholder="Select property type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((p) => (
                <SelectItem key={p.v} value={p.v}>
                  {p.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Size</Label>
          <Select value={form.size} onValueChange={set("size")}>
            <SelectTrigger
              data-testid="cleaning-form-size"
              className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
            >
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              {SIZE_OPTIONS.map((s) => (
                <SelectItem key={s.v} value={s.v}>
                  {s.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Location in Dubai</Label>
        <Select value={form.location} onValueChange={set("location")}>
          <SelectTrigger
            data-testid="cleaning-form-location"
            className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
          >
            <SelectValue placeholder="Select area" />
          </SelectTrigger>
          <SelectContent>
            {LOCATIONS.map((l) => (
              <SelectItem key={l} value={l}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Service Required *</Label>
        <Select value={form.serviceRequired} onValueChange={set("serviceRequired")}>
          <SelectTrigger
            data-testid="cleaning-form-service"
            className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
          >
            <SelectValue placeholder="Pick the service you need" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s.v} value={s.v}>
                {s.l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Preferred Date</Label>
        <Input
          data-testid="cleaning-form-date"
          type="date"
          value={form.preferredDate}
          onChange={update("preferredDate")}
          className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Notes</Label>
        <Textarea
          data-testid="cleaning-form-notes"
          value={form.notes}
          onChange={update("notes")}
          rows={3}
          placeholder="Anything we should know — access details, specific areas, pets…"
          className="rounded-lg border-slate-300 focus-visible:ring-blue-500"
        />
      </div>

      <button
        type="submit"
        data-testid="cleaning-form-submit"
        disabled={submitting}
        className="btn-primary-orion w-full disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            Get My Free Quote
            <ArrowRight className="w-4 h-4 rtl-flip" />
          </>
        )}
      </button>
      <p className="text-xs text-slate-500 text-center">
        We&apos;ll call within 30 minutes. No spam, no obligation.
      </p>
    </form>
  );
};
